import React, { useState, useEffect } from 'react';
import { Eye, MousePointerClick, TrendingUp, Users, Activity, RefreshCw, BarChart3, Target, Download, FileText, FileJson, GitCompare } from 'lucide-react';
import SummaryCard from '../components/dashboard/SummaryCard';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import DataTable from '../components/dashboard/DataTable';
import PerformanceTrendChart from '../components/performance/PerformanceTrendChart';
import MetricsComparisonChart from '../components/performance/MetricsComparisonChart';
import CtrChart from '../components/performance/CtrChart';
import FillRateChart from '../components/performance/FillRateChart';
import UsersSessionsChart from '../components/performance/UsersSessionsChart';
import AdUnitBreakdownChart from '../components/performance/AdUnitBreakdownChart';
import ComparisonCard from '../components/comparison/ComparisonCard';
import ComparisonChart from '../components/comparison/ComparisonChart';
import TrendInsights from '../components/trend/TrendInsights';
import { analyticsService } from '../services/analyticsService';
import { exportPerformanceData } from '../utils/exportUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Performance Overview Page
 * Displays comprehensive performance metrics and breakdowns
 */
const Performance = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [metricsData, setMetricsData] = useState([]);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Date range state (defaults to last 30 days)
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  });

  // Account ID state (optional, defaults to user's default account)
  const [accountId, setAccountId] = useState(null);

  // Chart configuration state
  const [chartType, setChartType] = useState('line'); // 'line', 'bar', 'area'
  const [period, setPeriod] = useState('daily'); // 'daily', 'weekly', 'monthly'
  const [metric, setMetric] = useState('impressions'); // 'impressions', 'clicks', 'ctr', 'fill_rate', 'completion_rate', 'active_users', 'sessions'

  // Fetch all performance data
  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        period: period,
        metric: metric,
      };
      
      if (accountId) {
        params.account_id = accountId;
      }

      // Fetch data in parallel
      const [metricsRes, trendsRes] = await Promise.all([
        analyticsService.getPerformanceMetrics({ ...params, per_page: 100 }).catch(() => ({ data: null })),
        analyticsService.getPerformanceTrends(params).catch(() => ({ data: null })),
      ]);

      // Process metrics data for summary
      if (metricsRes?.data?.data) {
        const data = metricsRes.data.data;
        setMetricsData(data);

        // Calculate summary statistics
        const totalImpressions = data.reduce((sum, row) => sum + (parseFloat(row.impressions) || 0), 0);
        const totalClicks = data.reduce((sum, row) => sum + (parseFloat(row.clicks) || 0), 0);
        const totalSessions = data.reduce((sum, row) => sum + (parseFloat(row.sessions) || 0), 0);
        const totalActiveUsers = data.reduce((sum, row) => sum + (parseFloat(row.active_users) || 0), 0);
        
        const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
        const avgFillRate = data.reduce((sum, row) => sum + (parseFloat(row.fill_rate) || 0), 0) / (data.length || 1);
        const avgCompletionRate = data.reduce((sum, row) => sum + (parseFloat(row.completion_rate) || 0), 0) / (data.length || 1);
        const avgImpressionsPerSession = totalSessions > 0 ? totalImpressions / totalSessions : 0;

        setSummary({
          total_impressions: totalImpressions,
          total_clicks: totalClicks,
          total_sessions: totalSessions,
          total_active_users: totalActiveUsers,
          average_ctr: avgCtr,
          average_fill_rate: avgFillRate,
          average_completion_rate: avgCompletionRate,
          impressions_per_session: avgImpressionsPerSession,
        });
      } else {
        setSummary(null);
      }

      // Process trends data
      if (trendsRes?.data?.trends) {
        setTrends(trendsRes.data);
      } else {
        setTrends(null);
      }

    } catch (err) {
      console.error('Error fetching performance data:', err);
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  };

  // Fetch comparison data
  const fetchComparisonData = async () => {
    if (!comparisonMode) {
      setComparisonData(null);
      return;
    }

    try {
      setComparisonLoading(true);
      
      // Calculate previous period dates (same duration as current period)
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      const previousEndDate = new Date(startDate);
      previousEndDate.setDate(previousEndDate.getDate() - 1);
      const previousStartDate = new Date(previousEndDate);
      previousStartDate.setDate(previousStartDate.getDate() - daysDiff);

      const params = {
        current_start_date: dateRange.startDate,
        current_end_date: dateRange.endDate,
        previous_start_date: previousStartDate.toISOString().split('T')[0],
        previous_end_date: previousEndDate.toISOString().split('T')[0],
      };

      if (accountId) {
        params.account_id = accountId;
      }

      const response = await analyticsService.getComparison(params);
      if (response.data) {
        setComparisonData(response.data);
      }
    } catch (err) {
      console.error('Error fetching comparison data:', err);
    } finally {
      setComparisonLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformanceData();
  }, [dateRange.startDate, dateRange.endDate, period, metric, accountId]);

  useEffect(() => {
    fetchComparisonData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonMode, dateRange.startDate, dateRange.endDate, accountId]);

  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange({ startDate, endDate });
  };

  const handleRefresh = () => {
    fetchPerformanceData();
  };

  // Export handlers
  const handleExportCSV = () => {
    exportPerformanceData(
      {
        summary,
        trends,
        metricsData,
      },
      'csv',
      {
        dateRange,
        accountId,
        period,
        metric,
        chartType,
      }
    );
  };

  const handleExportJSON = () => {
    exportPerformanceData(
      {
        summary,
        trends,
        metricsData,
      },
      'json',
      {
        dateRange,
        accountId,
        period,
        metric,
        chartType,
      }
    );
  };

  // Calculate chart data from trends
  const chartData = trends?.trends || [];

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="text-destructive text-sm sm:text-base">Error Loading Performance Data</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={handleRefresh} variant="outline" size="sm" className="text-xs">
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3 lg:mb-4 gap-2">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Performance Metrics</h1>
          <p className="text-muted-foreground text-xs">Monitor ad performance and engagement metrics</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={loading || !summary} className="text-xs">
                <Download className="w-3 h-3 mr-1" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV}>
                <FileText className="w-3 h-3 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportJSON}>
                <FileJson className="w-3 h-3 mr-2" />
                Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading} className="text-xs">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Picker & Comparison Toggle */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-sm sm:text-base">Date Range</CardTitle>
            <div className="flex items-center gap-2">
              <Checkbox
                id="comparison-mode"
                checked={comparisonMode}
                onCheckedChange={setComparisonMode}
              />
              <label
                htmlFor="comparison-mode"
                className="text-xs sm:text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5 cursor-pointer"
              >
                <GitCompare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                Compare with Previous Period
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange}
          />
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {comparisonMode && comparisonData ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
          <ComparisonCard
            title="Total Impressions"
            currentValue={comparisonData.current_period?.metrics?.impressions || summary?.total_impressions || 0}
            previousValue={comparisonData.previous_period?.metrics?.impressions || 0}
            format="number"
            icon={Eye}
            subtitle={`${dateRange.startDate} to ${dateRange.endDate}`}
          />
          <ComparisonCard
            title="Total Clicks"
            currentValue={comparisonData.current_period?.metrics?.clicks || summary?.total_clicks || 0}
            previousValue={comparisonData.previous_period?.metrics?.clicks || 0}
            format="number"
            icon={MousePointerClick}
            subtitle={`CTR: ${summary?.average_ctr?.toFixed(2) || 0}%`}
          />
          <ComparisonCard
            title="Click-Through Rate"
            currentValue={comparisonData.current_period?.metrics?.ctr || summary?.average_ctr || 0}
            previousValue={comparisonData.previous_period?.metrics?.ctr || 0}
            format="percentage"
            icon={TrendingUp}
            subtitle="Average CTR"
          />
          <ComparisonCard
            title="Fill Rate"
            currentValue={comparisonData.current_period?.metrics?.fill_rate || summary?.average_fill_rate || 0}
            previousValue={comparisonData.previous_period?.metrics?.fill_rate || 0}
            format="percentage"
            icon={Target}
            subtitle="Average fill rate"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
          <SummaryCard
            title="Total Impressions"
            value={summary?.total_impressions || 0}
            subtitle={`${dateRange.startDate} to ${dateRange.endDate}`}
            icon={Eye}
          />
          <SummaryCard
            title="Total Clicks"
            value={summary?.total_clicks || 0}
            subtitle={`CTR: ${summary?.average_ctr?.toFixed(2) || 0}%`}
            icon={MousePointerClick}
          />
          <SummaryCard
            title="Click-Through Rate"
            value={summary?.average_ctr || 0}
            subtitle="Average CTR"
            icon={TrendingUp}
            isPercentage={true}
          />
          <SummaryCard
            title="Fill Rate"
            value={summary?.average_fill_rate || 0}
            subtitle="Average fill rate"
            icon={Target}
            isPercentage={true}
          />
        </div>
      )}

      {/* Additional Metrics Cards */}
      {comparisonMode && comparisonData ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
          <ComparisonCard
            title="Completion Rate"
            currentValue={comparisonData.current_period?.metrics?.completion_rate || summary?.average_completion_rate || 0}
            previousValue={comparisonData.previous_period?.metrics?.completion_rate || 0}
            format="percentage"
            icon={Activity}
            subtitle="Video completion rate"
          />
          <ComparisonCard
            title="Active Users"
            currentValue={comparisonData.current_period?.metrics?.active_users || summary?.total_active_users || 0}
            previousValue={comparisonData.previous_period?.metrics?.active_users || 0}
            format="number"
            icon={Users}
            subtitle="Unique active users"
          />
          <ComparisonCard
            title="Total Sessions"
            currentValue={comparisonData.current_period?.metrics?.sessions || summary?.total_sessions || 0}
            previousValue={comparisonData.previous_period?.metrics?.sessions || 0}
            format="number"
            icon={Activity}
            subtitle={`${summary?.impressions_per_session?.toFixed(1) || 0} impressions/session`}
          />
          <ComparisonCard
            title="Impressions/Session"
            currentValue={summary?.impressions_per_session || 0}
            previousValue={comparisonData.previous_period?.metrics?.sessions > 0 && comparisonData.previous_period?.metrics?.impressions > 0 
              ? (comparisonData.previous_period.metrics.impressions / comparisonData.previous_period.metrics.sessions).toFixed(1) 
              : 0}
            format="number"
            icon={BarChart3}
            subtitle="Average per session"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
          <SummaryCard
            title="Completion Rate"
            value={summary?.average_completion_rate || 0}
            subtitle="Video completion rate"
            icon={Activity}
            isPercentage={true}
          />
          <SummaryCard
            title="Active Users"
            value={summary?.total_active_users || 0}
            subtitle="Unique active users"
            icon={Users}
          />
          <SummaryCard
            title="Total Sessions"
            value={summary?.total_sessions || 0}
            subtitle={`${summary?.impressions_per_session?.toFixed(1) || 0} impressions/session`}
            icon={Activity}
          />
          <SummaryCard
            title="Impressions/Session"
            value={summary?.impressions_per_session || 0}
            subtitle="Average per session"
            icon={BarChart3}
          />
        </div>
      )}

      {/* Performance Charts Section */}
      <div className="space-y-2 sm:space-y-3 lg:space-y-4 mb-2 sm:mb-3 lg:mb-4">
        {comparisonMode && comparisonData && comparisonData.current_period && comparisonData.previous_period ? (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm sm:text-base">Period Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Current Period</div>
                    <div className="text-lg font-semibold">
                      {dateRange.startDate} to {dateRange.endDate}
                    </div>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Previous Period</div>
                    <div className="text-lg font-semibold">
                      {comparisonData.previous_period.start_date} to {comparisonData.previous_period.end_date}
                    </div>
                  </div>
                </div>
                {comparisonData.changes && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-background rounded-lg border">
                      <div className="text-xs text-muted-foreground mb-1">Impressions Change</div>
                      <div className={`text-lg font-bold ${comparisonData.changes.impressions?.percentage_change > 0 ? 'text-green-600' : comparisonData.changes.impressions?.percentage_change < 0 ? 'text-red-600' : ''}`}>
                        {comparisonData.changes.impressions?.percentage_change > 0 ? '+' : ''}{comparisonData.changes.impressions?.percentage_change?.toFixed(2) || 0}%
                      </div>
                    </div>
                    <div className="text-center p-3 bg-background rounded-lg border">
                      <div className="text-xs text-muted-foreground mb-1">Clicks Change</div>
                      <div className={`text-lg font-bold ${comparisonData.changes.clicks?.percentage_change > 0 ? 'text-green-600' : comparisonData.changes.clicks?.percentage_change < 0 ? 'text-red-600' : ''}`}>
                        {comparisonData.changes.clicks?.percentage_change > 0 ? '+' : ''}{comparisonData.changes.clicks?.percentage_change?.toFixed(2) || 0}%
                      </div>
                    </div>
                    <div className="text-center p-3 bg-background rounded-lg border">
                      <div className="text-xs text-muted-foreground mb-1">CTR Change</div>
                      <div className={`text-lg font-bold ${comparisonData.changes.ctr?.percentage_change > 0 ? 'text-green-600' : comparisonData.changes.ctr?.percentage_change < 0 ? 'text-red-600' : ''}`}>
                        {comparisonData.changes.ctr?.percentage_change > 0 ? '+' : ''}{comparisonData.changes.ctr?.percentage_change?.toFixed(2) || 0}%
                      </div>
                    </div>
                    <div className="text-center p-3 bg-background rounded-lg border">
                      <div className="text-xs text-muted-foreground mb-1">Fill Rate Change</div>
                      <div className={`text-lg font-bold ${comparisonData.changes.fill_rate?.percentage_change > 0 ? 'text-green-600' : comparisonData.changes.fill_rate?.percentage_change < 0 ? 'text-red-600' : ''}`}>
                        {comparisonData.changes.fill_rate?.percentage_change > 0 ? '+' : ''}{comparisonData.changes.fill_rate?.percentage_change?.toFixed(2) || 0}%
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <PerformanceTrendChart
              data={chartData}
              trendData={trends}
              chartType={chartType}
              period={period}
              metric={metric}
              onChartTypeChange={setChartType}
              onPeriodChange={setPeriod}
              onMetricChange={setMetric}
            />
            <MetricsComparisonChart data={chartData} />
            <CtrChart data={chartData} />
            <FillRateChart data={chartData} />
            <UsersSessionsChart data={chartData} />
            <AdUnitBreakdownChart data={metricsData} />
          </>
        ) : (
          <>
            {trends && trends.summary && (
              <TrendInsights 
                summary={trends.summary} 
                metric={metric} 
                format={metric === 'ctr' || metric === 'fill_rate' || metric === 'completion_rate' ? 'percentage' : 'number'} 
              />
            )}
            <PerformanceTrendChart
              data={chartData}
              trendData={trends}
              chartType={chartType}
              period={period}
              metric={metric}
              onChartTypeChange={setChartType}
              onPeriodChange={setPeriod}
              onMetricChange={setMetric}
            />
            <MetricsComparisonChart data={chartData} />
            <CtrChart data={chartData} />
            <FillRateChart data={chartData} />
            <UsersSessionsChart data={chartData} />
            <AdUnitBreakdownChart data={metricsData} />
          </>
        )}
      </div>

      {/* Performance Data Table */}
      {metricsData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm sm:text-base">Performance Data</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              data={metricsData}
              columns={[
                {
                  accessorKey: 'date',
                  header: 'Date',
                  cell: ({ row }) => new Date(row.original.date).toLocaleDateString(),
                },
                {
                  accessorKey: 'app_key',
                  header: 'App',
                },
                {
                  accessorKey: 'ad_unit',
                  header: 'Ad Unit',
                },
                {
                  accessorKey: 'impressions',
                  header: 'Impressions',
                  cell: ({ row }) => parseInt(row.original.impressions || 0).toLocaleString(),
                },
                {
                  accessorKey: 'clicks',
                  header: 'Clicks',
                  cell: ({ row }) => parseInt(row.original.clicks || 0).toLocaleString(),
                },
                {
                  accessorKey: 'ctr',
                  header: 'CTR',
                  cell: ({ row }) => `${parseFloat(row.original.ctr || 0).toFixed(2)}%`,
                },
                {
                  accessorKey: 'fill_rate',
                  header: 'Fill Rate',
                  cell: ({ row }) => `${parseFloat(row.original.fill_rate || 0).toFixed(2)}%`,
                },
                {
                  accessorKey: 'completion_rate',
                  header: 'Completion Rate',
                  cell: ({ row }) => `${parseFloat(row.original.completion_rate || 0).toFixed(2)}%`,
                },
                {
                  accessorKey: 'active_users',
                  header: 'Active Users',
                  cell: ({ row }) => parseInt(row.original.active_users || 0).toLocaleString(),
                },
                {
                  accessorKey: 'sessions',
                  header: 'Sessions',
                  cell: ({ row }) => parseInt(row.original.sessions || 0).toLocaleString(),
                },
              ]}
              searchable={true}
              searchPlaceholder="Search by app, ad unit..."
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Performance;
